import { useState, useCallback } from 'react';
import API_ROUTES from './apiRoutes';
import { Content } from '../Models/Content';
import { useFetch } from './useFetch';

export const useContentList = () => {
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { fetchWithAuth } = useFetch();

    const getToken = () => localStorage.getItem("token");

    const fetchContents = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth(API_ROUTES.CONTENTS.list(), {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    "Content-Type": "application/json",
                }
            });
            if (!response.ok) {
                throw new Error('Falha ao carregar conteúdos');
            }
            const data = await response.json();

            const transformedContents = data.contents.map(content => Content.fromAPI(content));
            setContents(transformedContents);
            setError(null);
        } catch (err) {
            setError(err.message || 'Erro ao carregar conteúdos');
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const createContent = useCallback(async (contentData) => {
        try {
            setLoading(true);
            const content = new Content(contentData);
            const response = await fetchWithAuth(API_ROUTES.CONTENTS.create(), {
                method: 'POST',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(content.toAPI())
            });

            if (!response.ok) {
                throw new Error('Falha ao criar conteúdo');
            }

            await fetchContents();
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao criar conteúdo');
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchContents, fetchWithAuth]);

    const updateContent = useCallback(async (contentId, contentData) => {
        try {
            setLoading(true);
            const content = new Content({ id: contentId, ...contentData });
            const response = await fetchWithAuth(API_ROUTES.CONTENTS.update(contentId), {
                method: 'PUT',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(content.toAPI())
            });

            if (!response.ok) {
                throw new Error('Falha ao atualizar conteúdo');
            }

            await fetchContents();
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao atualizar conteúdo');
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchContents, fetchWithAuth]);

    const deleteContent = useCallback(async (contentId) => {
        try {
            setLoading(true);
            const response = await fetchWithAuth(API_ROUTES.CONTENTS.delete(contentId), {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    "Content-Type": "application/json",
                }
            });

            if (!response.ok) {
                throw new Error('Falha ao excluir conteúdo');
            }

            await fetchContents();
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao excluir conteúdo');
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchContents, fetchWithAuth]);

    return {
        contents,
        loading,
        error,
        createContent,
        updateContent,
        deleteContent,
        refreshContents: fetchContents
    };
}; 